import { Group, Material, Object3D, Vector3 } from 'three';
import type { ObjectSnapshot, SceneDiff } from '../scene-diff-manager';
import type { DebugManager } from '../debug-manager';
import type {
	IslandAnimationSequence,
	ObjectMetadata,
	SceneObject,
	TransitionableObjectType,
	TransitionConfigRegistry,
	TypeTransitionConfig
} from '../../types/engine.types';
import type { MaterialState, SceneTransitionManagerConfig } from './types';
import {
	animateAdditionByTypeCfn,
	animateAdditionsCfn,
	animateRemovalByTypeCfn,
	animateRemovalsCfn,
	getDefaultTransitionRegistry,
	processAdditionsByIslandCfn,
	processRemovalsByTypeCfn
} from './functions';

/**
 * SceneTransitionManager orchestrates animated transitions for scene updates.
 *
 * It handles three types of animations:
 * 1. Removal: Fade out + scale down, then remove from scene
 * 2. Move: Smooth position tween
 * 3. Addition: Fade in from transparent
 *
 * The animations are staged: removals complete before moves, moves before additions.
 */
export class SceneTransitionManager {
	#config: Required<Omit<SceneTransitionManagerConfig, 'objectHolder' | 'onCutoutRegenerationNeeded' | 'transitionRegistry' | 'islandSequence' | 'debugManager'>> & {
		objectHolder: Group | null;
		onCutoutRegenerationNeeded?: () => Promise<void>;
		debugManager?: DebugManager;
	};
	#animationCounter = 0;
	#isTransitioning = false;
	#materialStates = new Map<Material, MaterialState>();
	#transitionRegistry: TransitionConfigRegistry;
	#islandSequence: IslandAnimationSequence;

	// Getters for cfn function access
	get config () { return this.#config; }
	get materialStates (): Map<Material, MaterialState> { return this.#materialStates; }
	get transitionRegistry (): TransitionConfigRegistry { return this.#transitionRegistry; }
	get islandSequence (): IslandAnimationSequence { return this.#islandSequence; }

	/** Generate unique animation ID and increment counter */
	nextAnimationId (prefix: string): string {
		return `${prefix}-${this.#animationCounter++}`;
	}

	constructor (config: SceneTransitionManagerConfig) {
		this.#config = {
			fadeOutDuration: config.fadeOutDuration ?? 400,
			moveDuration: config.moveDuration ?? 600,
			fadeInDuration: config.fadeInDuration ?? 400,
			fadeOutScale: config.fadeOutScale ?? 0.8,
			animationManager: config.animationManager,
			objectHolder: config.objectHolder,
			onCutoutRegenerationNeeded: config.onCutoutRegenerationNeeded,
			debugManager: config.debugManager
		};
		this.#transitionRegistry = config.transitionRegistry ?? getDefaultTransitionRegistry();
		this.#islandSequence = config.islandSequence ?? {
			typeOrder: ['floor', 'panel', 'corner', 'trim', 'countertop', 'cutout', 'appliance'],
			sequential: true,
			parallelGroups: []
		};
	}

	setObjectHolder (objectHolder: Group | null): void {
		this.#config.objectHolder = objectHolder;
	}

	get isTransitioning (): boolean {
		return this.#isTransitioning;
	}

	setOnCutoutRegenerationNeeded (callback: () => Promise<void>): void {
		this.#config.onCutoutRegenerationNeeded = callback;
	}

	async executeTransition (diff: SceneDiff, onComplete?: () => void): Promise<void> {
		if (this.#isTransitioning) {
			console.warn('SceneTransitionManager: Transition already in progress, skipping');
			return;
		}
		this.#isTransitioning = true;
		const cutoutTypes = ['appliance', 'countertop', 'panel', 'surround', 'corner'];
		const needsCutoutRegeneration =
			diff.removed.some((s) => cutoutTypes.includes(s.type)) ||
			diff.added.some((s) => cutoutTypes.includes(s.metadata.type)) ||
			diff.moved.some((m) => cutoutTypes.includes(m.metadata.type));

		try {
			if (diff.removed.length > 0) await animateRemovalsCfn(this, diff.removed);
			if (diff.moved.length > 0) await this.animateMoves(diff.moved);
			if (diff.added.length > 0) await animateAdditionsCfn(this, diff.added);
			if (needsCutoutRegeneration && this.#config.onCutoutRegenerationNeeded) {
				await this.#config.onCutoutRegenerationNeeded();
			}
			onComplete?.();
		} catch (error) {
			console.error('SceneTransitionManager: Error during transition:', error);
		} finally {
			this.#isTransitioning = false;
		}
	}

	async animateMoves (moves: Array<{
		object: Object3D;
		from: { x: number; y: number; z: number };
		to: { x: number; y: number; z: number };
		metadata: ObjectMetadata;
	}>): Promise<void> {
		if (moves.length === 0) return;
		const { moveDuration, animationManager } = this.#config;
		const promises: Promise<void>[] = [];

		for (const move of moves) {
			const { object, from, to } = move;
			const animId = `move-${this.#animationCounter++}`;
			const fromVec = new Vector3(from.x, from.y, from.z);
			const toVec = new Vector3(to.x, to.y, to.z);

			// The diff speaks WORLD space at both ends — createObjectSnapshot stores
			// getWorldPosition(object) and computeDiff sets `to` from getWorldPosition too.
			// The callback below writes object.position, which is LOCAL to the object's
			// parent. Appliances hang off an island group carrying that island's origin
			// and Y rotation, so writing the world value straight in applies the island
			// transform a second time and the appliance lands at p + R·(p + R·local).
			// Measured on an L-shaped kitchen's second island: 70" off.
			// Additions are unaffected — they never write a position, they just attach
			// the builder's object — which is why only redistributions scatter.
			if (object.parent) {
				object.parent.updateWorldMatrix(true, false);
				object.parent.worldToLocal(fromVec);
				object.parent.worldToLocal(toVec);
			}

			const movePromise = animationManager.animatePositionAsync(
				animId,
				fromVec,
				toVec,
				moveDuration,
				(position) => object.position.copy(position)
			);
			promises.push(movePromise);
		}
		await Promise.all(promises);
	}

	animateRemovals (objects: ObjectSnapshot[]): Promise<void> {
		return animateRemovalsCfn(this, objects);
	}

	animateAdditions (objects: SceneObject[]): Promise<void> {
		return animateAdditionsCfn(this, objects);
	}

	cancelTransition (): void {
		this.#config.animationManager.stopAll();
		this.#isTransitioning = false;
	}

	getTransitionConfig (type: TransitionableObjectType): TypeTransitionConfig | undefined {
		return this.#transitionRegistry[type];
	}

	getTransitionRegistry (): TransitionConfigRegistry {
		return this.#transitionRegistry;
	}

	setTransitionRegistry (registry: TransitionConfigRegistry): void {
		this.#transitionRegistry = { ...this.#transitionRegistry, ...registry };
	}

	setIslandSequence (sequence: IslandAnimationSequence): void {
		this.#islandSequence = sequence;
	}

	getIslandSequence (): IslandAnimationSequence {
		return this.#islandSequence;
	}

	// eslint-disable-next-line max-params
	animateAdditionByType (
		sceneObject: SceneObject,
		animationCenter?: { x: number; y: number; z: number },
		isInitialRender = false,
		isWorldCoords = false,
		cameraPosition?: { x: number; y: number; z: number }
	): Promise<void> {
		return animateAdditionByTypeCfn(this, sceneObject, animationCenter, isInitialRender, isWorldCoords, cameraPosition);
	}

	animateRemovalByType (snapshot: ObjectSnapshot): Promise<void> {
		return animateRemovalByTypeCfn(this, snapshot);
	}

	/**
	 * Dissolve all provided objects using type-aware reverse-order removal.
	 * Objects are removed in reverse build order (appliances first, then structure).
	 */
	async dissolveAllByType (objects: ObjectSnapshot[]): Promise<void> {
		if (objects.length === 0) return;
		this.#isTransitioning = true;
		try {
			await processRemovalsByTypeCfn(this, objects);
		} finally {
			this.#isTransitioning = false;
		}
	}

	async executeTransitionByType (
		diff: SceneDiff,
		islandGroups: Map<string, { center: { x: number; y: number; z: number }; objects: SceneObject[] }>
	): Promise<void> {
		if (this.#isTransitioning) {
			console.warn('SceneTransitionManager: Transition already in progress, skipping');
			return;
		}
		this.#isTransitioning = true;

		try {
			if (diff.removed.length > 0) await processRemovalsByTypeCfn(this, diff.removed);
			if (diff.moved.length > 0) await this.animateMoves(diff.moved);
			if (diff.added.length > 0) await processAdditionsByIslandCfn(this, diff.added, islandGroups);

			const cutoutTypes = ['appliance', 'countertop', 'panel', 'surround', 'corner'];
			const needsCutoutRegeneration =
				diff.removed.some((s) => cutoutTypes.includes(s.type)) ||
				diff.added.some((s) => cutoutTypes.includes(s.metadata.type)) ||
				diff.moved.some((m) => cutoutTypes.includes(m.metadata.type));

			if (needsCutoutRegeneration && this.#config.onCutoutRegenerationNeeded) {
				await this.#config.onCutoutRegenerationNeeded();
			}
		} catch (error) {
			console.error('SceneTransitionManager: Error during type-aware transition:', error);
		} finally {
			this.#isTransitioning = false;
		}
	}
}
